<template>
  <div>
    <div class="tw-bg-white tw-shadow tw-border-t tw-border-gray-200">
      <base-content class="tw-pt-3 tw-pb-3.5">
        <base-breadcrumbs :breadcrumbs="breadcrumbs" />
      </base-content>
      <base-content class="tw-pb-3 tw-pt-0">
        <div class="tw-flex tw-justify-between tw-items-center">
          <h1 class="tw-text-xl sm:tw-text-2xl tw-leading-tight tw-text-gray-900 tw-mb-0 tw-font-thin tw-my-auto">Scheduled Activities for {{ businessPlanName }}</h1>
          <div class="tw-my-auto">
            <base-button @click="add" class="tw-hidden sm:tw-flex">
              <template v-slot:icon>
                <plus-icon />
              </template>
              Add Activity
            </base-button>
            <base-icon @click="add" class="tw-inline sm:tw-hidden">
              <plus-icon class="tw-h-5 tw-w-5" />
            </base-icon>
          </div>
        </div>
      </base-content>
    </div>
    <base-content class="tw-mt-3">
      <div>
        <div class="tw-mb-2 tw-font-normal">Plan created</div>
        <base-list-group>
          <base-list-item v-for="(activity, index) in createSchedule" :key="activity.businessPlanActivitySchduleId" :showBottomBorder="index != createSchedule.length - 1">
            <div class="tw-flex tw-justify-between">
              <div>
                <div class="tw-mb-1">{{ activity.todoItemTypeName }}</div>
                <div class="tw-text-gray-600 tw-text-xs">Schedule {{ activity.daysAfter }} days after {{ getTriggerLabel(activity.trigger) }}</div>
              </div>
              <div class="tw-my-auto">
                <base-button class="tw-mr-1" type="flat" size="sm" @click="edit(activity)">
                  <template v-slot:icon>
                    <edit-icon />
                  </template>
                  Edit
                </base-button>
                <base-button type="flat" size="sm" @click="del(activity)">
                  <template v-slot:icon>
                    <trash-icon />
                  </template>
                  Delete
                </base-button>
              </div>
            </div>
          </base-list-item>
          <base-list-item v-if="createSchedule.length === 0 && isLoading === false" :showBottomBorder="false">
            <div class="tw-text-center tw-italic tw-text-gray-700 tw-text-sm">No Activities Scheduled</div>
          </base-list-item>
          <base-list-item v-if="isLoading === true">
            <div class="tw-flex tw-justify-between">
              <div>
                <base-skeleton-loader class="tw-h-5 tw-w-36 tw-mb-2" />
                <base-skeleton-loader class="tw-h-3 tw-w-56" />
              </div>
              <div class="tw-flex tw-my-auto tw-gap-5">
                <base-skeleton-loader class="tw-h-8 tw-w-12" />
                <base-skeleton-loader class="tw-h-8 tw-w-16 tw-mr-3" />
              </div>
            </div>
          </base-list-item>
          <base-list-item v-if="isLoading === true" :showBottomBorder="false">
            <div class="tw-flex tw-justify-between">
              <div>
                <base-skeleton-loader class="tw-h-5 tw-w-36 tw-mb-2" />
                <base-skeleton-loader class="tw-h-3 tw-w-56" />
              </div>
              <div class="tw-flex tw-my-auto tw-gap-5">
                <base-skeleton-loader class="tw-h-8 tw-w-12" />
                <base-skeleton-loader class="tw-h-8 tw-w-16 tw-mr-3" />
              </div>
            </div>
          </base-list-item>
        </base-list-group>
      </div>
      <div class="tw-mt-7">
        <div class="tw-mb-2 tw-font-normal">Plan canceled</div>
        <base-list-group>
          <base-list-item v-for="(activity, index) in cancelSchedule" :key="activity.businessPlanActivitySchduleId" :showBottomBorder="index != cancelSchedule.length - 1">
            <div class="tw-flex tw-justify-between">
              <div>
                <div class="tw-mb-1">{{ activity.todoItemTypeName }}</div>
                <div class="tw-text-gray-600 tw-text-xs">Schedule {{ activity.daysAfter }} days after {{ getTriggerLabel(activity.trigger) }}</div>
              </div>
              <div class="tw-my-auto">
                <base-button class="tw-mr-1" type="flat" size="sm" @click="edit(activity)">
                  <template v-slot:icon>
                    <edit-icon />
                  </template>
                  Edit
                </base-button>
                <base-button type="flat" size="sm" @click="del(activity)">
                  <template v-slot:icon>
                    <trash-icon />
                  </template>
                  Delete
                </base-button>
              </div>
            </div>
          </base-list-item>
          <base-list-item v-if="cancelSchedule.length === 0 && isLoading === false" :showBottomBorder="false">
            <div class="tw-text-center tw-italic tw-text-gray-700 tw-text-sm">No Activities Scheduled</div>
          </base-list-item>
          <base-list-item v-if="isLoading === true">
            <div class="tw-flex tw-justify-between">
              <div>
                <base-skeleton-loader class="tw-h-5 tw-w-36 tw-mb-2" />
                <base-skeleton-loader class="tw-h-3 tw-w-56" />
              </div>
              <div class="tw-flex tw-my-auto tw-gap-5">
                <base-skeleton-loader class="tw-h-8 tw-w-12" />
                <base-skeleton-loader class="tw-h-8 tw-w-16 tw-mr-3" />
              </div>
            </div>
          </base-list-item>
          <base-list-item v-if="isLoading === true" :showBottomBorder="false">
            <div class="tw-flex tw-justify-between">
              <div>
                <base-skeleton-loader class="tw-h-5 tw-w-36 tw-mb-2" />
                <base-skeleton-loader class="tw-h-3 tw-w-56" />
              </div>
              <div class="tw-flex tw-my-auto tw-gap-5">
                <base-skeleton-loader class="tw-h-8 tw-w-12" />
                <base-skeleton-loader class="tw-h-8 tw-w-16 tw-mr-3" />
              </div>
            </div>
          </base-list-item>
        </base-list-group>
      </div>
    </base-content>
    <new-scheduled-activity :businessId="businessId" :businessPlanId="businessPlanId" ref="newScheduledActivityModal" @scheduledActivitiesUpdated="getActivitySchedule" />
    <edit-scheduled-activity :businessId="businessId" :businessPlanId="businessPlanId" ref="editScheduledActivityModal" @scheduledActivitiesUpdated="getActivitySchedule" />
    <delete-scheduled-activity :businessId="businessId" :businessPlanId="businessPlanId" ref="deleteScheduledActivityModal" @scheduledActivitiesUpdated="getActivitySchedule" />
  </div>
</template>
<script>
import { PlusIcon, EditIcon, TrashIcon } from "@/assets/svgs";
import NewScheduledActivity from "@/views/business/business-plans/components/NewScheduledActivity";
import EditScheduledActivity from "@/views/business/business-plans/components/EditScheduledActivity";
import DeleteScheduledActivity from "@/views/business/business-plans/components/DeleteScheduledActivity";

export default {
  name: "BusinessPlanActivitySchedule",
  props: {
    businessId: String,
    businessPlanId: String,
    businessPlanName: String,
  },
  components: {
    PlusIcon,
    EditIcon,
    TrashIcon,
    NewScheduledActivity,
    EditScheduledActivity,
    DeleteScheduledActivity,
  },
  data() {
    return {
      isLoading: false,
      createSchedule: [],
      cancelSchedule: [],
      triggerOptions: [{ label: "plan is created", value: "plan_create" }, { label: "plan is cancelled", value: "plan_cancel" }],
    };
  },
  created() {
    this.getActivitySchedule();
  },
  computed: {
    breadcrumbs() {
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      return [{ title: "Offered Plans", onClick: () => this.$router.push(`/businesses/${this.businessId}/offerings/plans`) }, { title: this.businessPlanName }];
    },
  },
  methods: {
    getActivitySchedule() {
      this.isLoading = true;

      let url = `/businesses/${this.businessId}/plans/${this.businessPlanId}/activity-schedule`;

      this.$axios
        .get(url)
        .then((res) => {
          this.createSchedule = res.data.filter((s) => s.trigger === "plan_create").sort((a, b) => (b.daysAfter < a.daysAfter ? 1 : -1));
          this.cancelSchedule = res.data.filter((s) => s.trigger === "plan_cancel").sort((a, b) => (b.daysAfter < a.daysAfter ? 1 : -1));
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: `Failed to load scheduled activities`,
            message: `We had a problem retrieving the list of scheduled activities. Please reload the page to try again.`,
            error: err,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    add(item) {
      this.$refs.newScheduledActivityModal.show(item);
    },
    edit(activity) {
      this.$refs.editScheduledActivityModal.show(activity.businessPlanActivityScheduleId, activity.todoItemTypeId, activity.trigger, activity.daysAfter, activity.note);
    },
    del(activity) {
      this.$refs.deleteScheduledActivityModal.show(activity.businessPlanActivityScheduleId);
    },
    back() {
      this.$router.back();
    },
    getTriggerLabel(trigger) {
      let triggerOption = this.triggerOptions.find((x) => x.value === trigger);
      return triggerOption.label;
    },
  },
};
</script>
<style scoped>
/* for mobile screens */
@media screen and (min-width: 768px) {
  .dummy-tw-margin {
    margin-left: -55px;
    margin-right: -55px;
    margin-top: -51px;
  }
}
</style>
